import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import AuthVisual from '@/components/AuthVisual';
import MfaLoginStep from '@/components/mfa/MfaLoginStep';
import '@/styles/auth.css';

function formatError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || 'Une erreur est survenue';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join('. ');
  return String(d);
}

function Toast({ message, type, show }) {
  return (
    <div
      className={`c2s-toast ${show ? 'on' : ''} ${type === 'success' ? 'toast--success' : ''}`}
      role="status"
      aria-live="polite"
    >
      {message}
    </div>
  );
}

export default function LoginPage() {
  const { login, completeLogin, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get('return');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  // Double authentification (T15) : réponse de la connexion quand le mot de passe ne suffit pas
  const [challenge, setChallenge] = useState(null);

  const [emailErr, setEmailErr] = useState(false);
  const [pwdErr, setPwdErr] = useState(false);
  const [emailOk, setEmailOk] = useState(false);

  const [toast, setToast] = useState({ message: '', type: 'default', show: false });
  const toastTimer = useRef(null);

  useEffect(() => {
    if (user) {
      if (returnTo && returnTo.startsWith('/')) {
        navigate(returnTo);
      } else {
        navigate(user.roles?.includes('ROLE_ADMIN') ? '/admin/dashboard' : '/app/dashboard');
      }
    }
  }, [user, navigate, returnTo]);

  const showToast = (message, type = 'default') => {
    setToast({ message, type, show: true });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      setToast(t => ({ ...t, show: false }));
    }, 2800);
  };

  const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const onEmailChange = (e) => {
    const v = e.target.value;
    setEmail(v);
    if (v.length > 0) {
      const ok = validateEmail(v);
      setEmailOk(ok);
      setEmailErr(!ok);
    } else {
      setEmailOk(false);
      setEmailErr(false);
    }
  };

  const onPwdChange = (e) => {
    setPassword(e.target.value);
    if (pwdErr) setPwdErr(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let valid = true;
    if (!validateEmail(email)) {
      setEmailErr(true);
      valid = false;
    }
    if (!password) {
      setPwdErr(true);
      valid = false;
    }
    if (!valid) {
      showToast('Veuillez corriger les erreurs du formulaire');
      return;
    }

    setLoading(true);
    try {
      const data = await login(email, password, remember);
      if (data.mfa_required || data.mfa_setup_required) {
        setChallenge(data);
        setLoading(false);
        return;
      }
      setSuccess(true);
      showToast('Bienvenue sur Colis2Ship ! ✓', 'success');
      setTimeout(() => {
        if (returnTo && returnTo.startsWith('/')) {
          navigate(returnTo);
        } else {
          navigate(data.roles?.includes('ROLE_ADMIN') ? '/admin/dashboard' : '/app/dashboard');
        }
      }, 900);
    } catch (err) {
      const msg = formatError(err);
      if (msg.toLowerCase().includes('mot de passe') || msg.toLowerCase().includes('incorrect')) {
        setPwdErr(true);
      } else if (msg.toLowerCase().includes('email')) {
        setEmailErr(true);
      } else {
        setEmailErr(true);
        setPwdErr(true);
      }
      showToast(msg);
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout" data-testid="login-page">
      {/* ══ GAUCHE — Formulaire ══ */}
      <div className="form-side">
        <div className="form-wrap">
          <Link to="/" className="back-link" data-testid="back-home">← Retour à l'accueil</Link>

          <div className="brand">
            <div className="brand-mark">C2</div>
            <span className="brand-name">Colis<em>2</em>Ship</span>
          </div>

          {challenge ? (
            <MfaLoginStep challenge={challenge} onDone={completeLogin}
              onCancel={() => { setChallenge(null); setPassword(''); }} />
          ) : (<>
          <div className="form-hd">
            <h1 className="form-title">Bon retour 👋</h1>
            <p className="form-sub">Connectez-vous à votre espace Colis2Ship</p>
          </div>

          <div className="chip-secure">
            <span className="chip-dot" />
            Connexion sécurisée
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="fg">
              <label className="flbl" htmlFor="lEmail">Email</label>
              <div className="fi-wrap">
                <input
                  type="email"
                  id="lEmail"
                  className={`fi ${emailErr ? 'err' : ''} ${emailOk ? 'ok' : ''}`}
                  placeholder="votre@email.com"
                  autoComplete="email"
                  value={email}
                  onChange={onEmailChange}
                  data-testid="input-email"
                />
                <span className="fi-ico">✉</span>
              </div>
              {emailErr && <div className="ferr on" data-testid="email-error">Adresse email invalide ou introuvable</div>}
            </div>

            <div className="fg">
              <label className="flbl" htmlFor="lPwd">Mot de passe</label>
              <div className="fi-wrap">
                <input
                  type={showPass ? 'text' : 'password'}
                  id="lPwd"
                  className={`fi ${pwdErr ? 'err' : ''}`}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={onPwdChange}
                  data-testid="input-password"
                />
                <button
                  type="button"
                  className="fi-ico fi-ico--eye"
                  onClick={() => setShowPass(s => !s)}
                  aria-label={showPass ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  data-testid="btn-toggle-password"
                >
                  {showPass ? '🙈' : '👁'}
                </button>
              </div>
              {pwdErr && <div className="ferr on" data-testid="password-error">Mot de passe incorrect</div>}
            </div>

            <div className="lnk-row">
              <label className="rmb">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                  style={{ accentColor: 'var(--c2s-glt)' }}
                  data-testid="checkbox-remember"
                />
                Se souvenir de moi
              </label>
              <Link to="/forgot-password" className="lnk" data-testid="link-forgot">Mot de passe oublié ?</Link>
            </div>

            <button
              type="submit"
              className="cta cta--primary"
              disabled={loading || success}
              style={success ? { background: 'var(--c2s-teal)', color: '#fff' } : undefined}
              data-testid="btn-login-submit"
            >
              {loading && <span className="cta-ldr" />}
              <span>{success ? 'Connecté ✓' : loading ? 'Connexion…' : 'Se connecter'}</span>
            </button>
          </form>

          <p className="sw">
            Pas encore de compte ?{' '}
            <Link to="/register" data-testid="link-register">Créer un compte gratuitement</Link>
          </p>
          </>)}
        </div>
      </div>

      {/* ══ DROITE — Visuel ══ */}
      <AuthVisual variant="login" />

      <Toast message={toast.message} type={toast.type} show={toast.show} />
    </div>
  );
}
